import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserStore } from '../stores/StoreProvider';

const OnboardingRole = observer(() => {
  const navigate = useNavigate();
  const userStore = useUserStore();
  const [selectedRole, setSelectedRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roles = [
    {
      id: 'player/parent',
      title: 'Player/Parent',
      description: 'I play hockey or have a child who plays',
      icon: '🏒',
      color: 'bg-blue-500'
    },
    {
      id: 'coach',
      title: 'Coach',
      description: 'I coach a team and want to manage players',
      icon: '📋',
      color: 'bg-green-500'
    }
  ];

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    setIsSubmitting(true);
    try {
      // Store role in onboarding data
      userStore.onboardingData = {
        ...userStore.onboardingData,
        role: selectedRole,
      };
      
      console.log('Role saved to onboarding data:', selectedRole);
      
      toast.success('Role saved!');
      navigate('/onboarding/position');
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-2xl">B</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            What's your role?
          </h2>
          <p className="text-blue-100">
            Help us personalize your experience
          </p>
        </div>

        {/* Role Selection */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-4">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  selectedRole === role.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 ${role.color} rounded-lg flex items-center justify-center text-white text-2xl`}>
                    {role.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900">{role.title}</h3>
                    <p className="text-sm text-gray-600">{role.description}</p>
                  </div>
                  {selectedRole === role.id && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            <button
              onClick={() => navigate('/onboarding/avatar')}
              className="flex-1 flex justify-center items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={!selectedRole || isSubmitting}
              className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          </div>
          <p className="text-white/80 text-sm mt-2">Step 3 of 5</p>
        </div>
      </div>
    </div>
  );
});

export default OnboardingRole; 